import { useState, useEffect, FC, useRef } from 'react';
import { createRoot } from 'react-dom/client';

import Consts from "Interfaces/Consts";
import Helper from 'Interfaces/Helper';
import { BackColor } from "Interfaces/general.interface";
import ComponentBackground from "GeneralComponents/ComponentBackground"
import SideLayoutForm from "./ContactUs_SideLayoutForm";
import FullWidthForm from "./ContactUs_FullWidthForm";
import ContactUsHeader from './ContactUs_Header';
import ErrorMessageComponent from './ContactUs_Error';
import SuccessMessageComponent from './ContactUs_Success';
import SubmitButton from './ContactUs_SubmitButton';
import RecaptchaComponent from './RecaptchaComponent';
import ContactSubItemComponent from './ContactUs_ContactSubItem';
import { FormTypeComponentProps, IconInfo, ContactUsProps as IProps } from './ContactUs.Interfaces';
import './ContactUs.min.css';


const FORM_TYPES = { fullWidth: "FullWidth", sideLayout: "SideLayout" };
const STATUS_TYPES = { success: "success", error: "error" }

const ContactUs: FC<IProps> = (props) => {

    const { componentId, currentPageId, componentTitle, componentDescription, siteKey, secondDescription } = props;

    const backGround: BackColor = props.backColor ? JSON.parse(props.backColor) : null;
    const dir = getComputedStyle(document.documentElement).direction;
    const hasTitle = (componentTitle !== '' || secondDescription !== '' || componentDescription !== '');
    const formType = props.formType || FORM_TYPES.fullWidth;
    const isSideLayout = (formType === FORM_TYPES.sideLayout);
    const isIconMenuInPallete = !!props.isIconMenuInPallete;
    const hideName = !!props.hideName;
    const hidePhone = !!props.hidePhone;
    const isVariable1Req = !!props.isVariable1Req;
    const isVariable2Req = !!props.isVariable2Req;
    let componentNumber = 0;
    const iconArr: IconInfo[] = props.contactSubItems ? JSON.parse(props.contactSubItems) : [];
    const variableInputs = [props.variableInput1.substring(0, 19), props.variableInput2.substring(0, 19)];
    const inputValuesInit = {
        name: '',
        email: '',
        phone: '',
        message: '',
        variable1: '',
        variable2: '',
    };

    // State Vars:
    const [isSuccess, setIsSuccess] = useState('');
    const [lightBg, setLightBg] = useState(true);
    const [errors, setErrors] = useState({});
    const [inputValues, setInputValues] = useState({ ...inputValuesInit });

    const isSubmitting = useRef(false); 

    // Functions
    const updateInputValue = (inputName: string, value: string) => {
        setInputValues({ ...inputValues, [inputName]: value })
    };

    const handleInputValueChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, inputName: string) => {
        try {
            if (event.target.value) {
                event.target.classList.add("valid")
                event.target.classList.remove("input-validation-error");
                setErrors({ ...errors, [inputName]: '' })
            }
            updateInputValue(inputName, event.currentTarget.value);
        }
        catch (e) {
            console.log("exception: " + e.message);
        }
    }

    const validateInput = (event, inputName: string) => {

        const regObj = {
            name: new RegExp(/^[A-Za-zא-תЁёА-я \u00C0-\u00FF\u0600-\u06FF '\ \-""]{1,100}$/),
            email: new RegExp(/^(?!.{200})([a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+)$/),
            phone: new RegExp(/^[\d\-\+\(\)]{7,20}$/),
            message: new RegExp(/^[A-Za-zא-תЁёА-я \u00C0-\u00FF\u0600-\u06FF 0-9\r\n\t?!. ,:'\ %$;\-@()*/""=&]{1,2000}$/),
            variable1: new RegExp(/^[A-Za-zא-תЁёА-я \u00C0-\u00FF\u0600-\u06FF 0-9\r\n\t?!. ,:'\ %$;\-@()*/""=&]{1,2000}$/),
            variable2: new RegExp(/^[A-Za-zא-תЁёА-я \u00C0-\u00FF\u0600-\u06FF 0-9\r\n\t?!. ,:'\ %$;\-@()*/""=&]{1,2000}$/)
        };

        const errorConst = {
            name: Consts.invalidCharactersName,
            email: Consts.invalidCharactersEmail,
            phone: Consts.invalidCharactersPhone,
            message: Consts.invalidCharactersMessage,
            variable1: variableInputs[0] + " " + Consts.invalidVariableInput,
            variable2: variableInputs[1] + " " + Consts.invalidVariableInput
        };

        if (inputValues[inputName]) {
            if (!regObj[inputName].test(inputValues[inputName])) {
                event.target.classList.remove("valid")
                event.target.classList.add("input-validation-error")
                setErrors({ ...errors, [inputName]: errorConst[inputName] });
            }
            else {
                event.target.classList.remove("input-validation-error");
                setErrors({ ...errors, [inputName]: '' });
            }
        }
    }

    const handleSubmit = (event) => {
        event.preventDefault();
        if (!isSubmitting.current && validateForm()) {
            isSubmitting.current = true;
            window.handleRecaptcha = handleRecaptcha;
            window.grecaptcha.execute();
        }
    }

    const handleRecaptcha = (token: string) => {

        document.querySelector(".mst-send-btn").classList.add("d-none");
        document.querySelector(".mst-loader-btn").classList.remove("d-none");

        try {
            fetch("/api/ContactForm", {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    currentPageId,
                    componentNumber,
                    name: inputValues.name,
                    mail: inputValues.email,
                    phone: inputValues.phone,
                    message: inputValues.message,
                    variableInput1: inputValues.variable1,
                    variableInput2: inputValues.variable2,
                    GRecaptchaResponse: token
                })
            }).then((response) => {
                if (response.status === 200) {
                    response.json().then(parsedJson => {

                        const successType = parsedJson.isSuccess;
                        setIsSuccess(successType);
                        if (successType != STATUS_TYPES.success) {
                            Helper.handleLinkClicksGA4('ContactUs', componentTitle, 'Send-Failed');
                        }
                        else {
                            Helper.handleLinkClicksGA4('ContactUs', componentTitle, 'Send-Succeed');
                        }
                    })
                }
                else {
                    setIsSuccess('');
                    Helper.handleLinkClicksGA4('ContactUs', componentTitle, 'Send-Failed');
                }
            })
        }
        catch (err) {
            console.log(err);
        }
        finally {
            isSubmitting.current = false;
        }
    }

    const validateForm = () => {

        //create a list of input to check if empty
        const inputsToCheck = ["email", "message"]
        const activeInputsErrors = {};

        if (!hideName) inputsToCheck.push("name")
        if (!hidePhone) inputsToCheck.push("phone")
        if (isVariable1Req && variableInputs[0]) inputsToCheck.push("variable1")
        if (isVariable2Req && variableInputs[1]) inputsToCheck.push("variable2")

        for (let input of inputsToCheck) {

            if (!inputValues[input]) {

                let variableInputName = "";

                if (input === "variable1"){ variableInputName = variableInputs[0] }
                else if (input === "variable2"){ variableInputName = variableInputs[1] }
                else { variableInputName = Consts[input] }

                activeInputsErrors[input] = Consts.requiredField + " " + variableInputName;
                const capitalizedInput = input.charAt(0).toUpperCase() + input.slice(1)
                const element = document.getElementById(capitalizedInput + "-" + componentId)
                element.classList.add("input-validation-error")
            }
        }

        const updatedErrors = { ...errors, ...activeInputsErrors };
        const formIsValid = Object.values(updatedErrors).every(v => v == '');
        setErrors(updatedErrors);
        return formIsValid;
    }

    const clearFormValues = () => setInputValues({ ...inputValuesInit });

    const closeMsg = () =>{
        const contactForm = document.getElementById("contact-form-" + componentId);
        const contactFormSuccess = document.getElementById("contact-success-" + componentId);
        const contactFormError = document.getElementById("contact-error-" + componentId);
        //remove the valid class from all inputs, so it would reset after sending
        const inputs = [...contactForm.querySelectorAll('input')];
        inputs.pop();
        inputs.push(contactForm.querySelector('#Message-' + componentId))
        inputs.forEach(input => {
            input.classList.remove('valid')
        })

        document.querySelector(".mst-send-btn").classList.remove("d-none");
        document.querySelector(".mst-loader-btn").classList.add("d-none");

        try{
            contactForm.classList.remove("d-none");
            contactFormSuccess.classList.add("d-none");
            contactFormError.classList.add("d-none");
            clearFormValues();
            setIsSuccess('');
        }
        catch (e){
            console.log("exception: " + e.message);
        }
    }

    const onComponentMount = () => {

        var contactForms = document.querySelectorAll('[id*="contact-form-"]');

        if (contactForms.length > 1) {
            for (let i = 0; i < contactForms.length; i++) {
                if (contactForms[i].getAttribute("id") == "contact-form-" + componentId) {
                    componentNumber = i;
                }
            }
        }
    }

    const classForMsg = () => { 
        // a class to make the message not be smaller than the form. we calculate inputs and add min height
        if (formType === FORM_TYPES.sideLayout) {
            let inputs = 0 + (!hideName && 1) + (!hidePhone && 1) + ((variableInputs[0] || variableInputs[1]) ? 1 : 0);
            return " min-h" + inputs;
        }
        else {
            let inputs = 1 + ((!hideName || !hidePhone) ? 1 : 0) + ((variableInputs[0] || variableInputs[1]) ? 1 : 0)
            return " min-v" + inputs
        }
    }

    const areIcons = () => {
        return iconArr.some(iconInfo =>
            iconInfo.IconTitle !== '' ||
            (iconInfo.Icon !== '' && iconInfo.Icon !== 'icon-MSite--146 mirror-for-heb') ||
            iconInfo.IconContent !== ''
        )
    }

    const classForIconArea = () => {
        return "icon-area " + (areIcons() ? "" : "hidden-icon-area ") +
            (isIconMenuInPallete ? "bg-pallete-color" : "icon-default")
    }

    // On Component Mount
    useEffect(() => {
        onComponentMount();
    }, []);

    const formTypeComponentProps: FormTypeComponentProps = {
        componentId,
        inputValues,
        variableInputs,
        changeInput: handleInputValueChange,
        validateInput,
        errors,
        hidePhone,
        hideName,
        isVariable1Req,
        isVariable2Req
    }

    const headerProps = {
        componentDescription,
        componentTitle, 
        secondDescription,
        classForIconArea: classForIconArea(),
        hasTitle,
        isLightBg:lightBg,
        icons: iconArr,
        isSideLayout,
    }

    const formWrapperClass = isSideLayout ? 'pb-3 pb-md-5' : 'pt-2 pt-lg-5 pb-3 pb-md-0';
    const displayForm: boolean = isSuccess == STATUS_TYPES.success || isSuccess == STATUS_TYPES.error;

    return (
        <ComponentBackground
            componentId={"ContactForm-" + componentId}
            backGround={backGround}
            setLightBg={setLightBg}
        >
            <article className="pt-3 pb-3">

                {
                    // Header for FullWidthLayout Form
                    !isSideLayout && <ContactUsHeader {...headerProps} />
                }

                <div className="container">

                    <div id={componentId} className={`mst-ContactForm ${isSideLayout ? "horizontal-form " : ""}`}>

                        {
                            // Header for SideLayout Form
                            isSideLayout && <ContactUsHeader {...headerProps} />
                        }

                        {
                            // FullWidth Layout icons display (inner box)
                            !isSideLayout && iconArr.length > 0 &&
                            < div className={headerProps.classForIconArea}>
                                {
                                    iconArr.map((icon, idx) =>
                                        <ContactSubItemComponent
                                            key={idx}
                                            iconInfo={icon}
                                            showIcon={!!iconArr[0].Icon}
                                            hasTopSeperatorLine={idx !== 0} />
                                    )
                                }
                            </div>
                        }

                        <form id={`contact-form-${componentId}`} onSubmit={handleSubmit}
                            className={`${displayForm ? 'd-none' : ''} ${formWrapperClass}`}>

                            {
                                isSideLayout ?
                                    <SideLayoutForm {...formTypeComponentProps } />
                                    :
                                    <FullWidthForm {...formTypeComponentProps } />
                            }

                            <SubmitButton />

                            <RecaptchaComponent
                                siteKey={siteKey}
                                callbackName={"handleRecaptcha"}
                                errorCallbackName={"handleRecaptchaError"}
                            />

                        </form>

                        <SuccessMessageComponent
                            componentId={componentId}
                            show={isSuccess == STATUS_TYPES.success}
                            closeMsg={closeMsg}
                            classForMsg={classForMsg}
                        />

                        <ErrorMessageComponent
                            componentId={componentId}
                            show={isSuccess == STATUS_TYPES.error}
                            closeMsg={closeMsg}
                            classForMsg={classForMsg}
                        />

                    </div>

                </div>

            </article>

        </ComponentBackground>
    )
}

var elements = document.querySelectorAll('[id^="ContactForm-"]');

for (var i = 0; i < elements.length; i++){
    var rootElement = document.getElementById(elements[i].id);
    var root = createRoot(rootElement);
    root.render(
        <ContactUs
            componentId={rootElement.getAttribute('component-id')}
            currentPageId={rootElement.getAttribute('component-currentPageId')}
            componentTitle={rootElement.getAttribute('component-title')}
            componentDescription={rootElement.getAttribute('component-description')}
            backColor={rootElement.getAttribute('component-backColor')}
            hidePhone={rootElement.getAttribute('component-hidePhone')}
            hideName={rootElement.getAttribute('component-hideName')}
            isIconMenuInPallete={rootElement.getAttribute('component-isIconMenuInPallete')}
            variableInput1={rootElement.getAttribute('component-variableInput1')}
            variableInput2={rootElement.getAttribute('component-variableInput2')}
            isVariable1Req={rootElement.getAttribute('component-isVariable1Req')}
            isVariable2Req={rootElement.getAttribute('component-isVariable2Req')}
            secondDescription={rootElement.getAttribute('component-secondDescription')}
            formType={rootElement.getAttribute('component-type')}
            contactSubItems={rootElement.getAttribute('component-contactSubItems')}
            siteKey={rootElement.getAttribute('component-siteKey')}
        />)
}